import { useCallback, useRef, useState } from 'react';

/**
 * The workspace's shared in-flight write guard for a SINGLE control (SL-0046, R3/R5).
 *
 * The guard is deliberately two-part, and neither part substitutes for the other:
 *
 * - `inFlight` is a ref checked and set SYNCHRONOUSLY inside `run`. That synchronous check is
 *   the only thing that stops a second activation arriving before React commits the `pending`
 *   state — the exact window in which one intended click produced three concurrent POSTs and
 *   three corrupted `clinical_studies` rows on 2026-08-04. `react-hook-form@7.80.0`'s
 *   `handleSubmit` has no re-entrancy early-return, so `formState.isSubmitting` CANNOT replace
 *   the ref: a second submit that lands before the commit still reaches the handler.
 * - `pending` is the state the user can see. Passed to a control's `disabled`,
 *   `components/ui/button.tsx`'s `disabled:pointer-events-none disabled:opacity-40` turns it
 *   into a visible, non-clickable in-flight affordance once committed.
 *
 * The `finally` is unconditional: a rejected write must re-enable the control, or one failed
 * request leaves the surface permanently dead.
 */
export function usePendingWrite(): {
  pending: boolean;
  run: (write: () => Promise<void>) => Promise<void>;
} {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async (write: () => Promise<void>) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      await write();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }, []);

  return { pending, run };
}

/**
 * The keyed variant of the same guard, for PER-ROW controls rendered in a list (SL-0046, R4).
 *
 * A single boolean is wrong for a list: it would disable every row's controls while any one
 * row is written. The key is the identifier of the ENTITY the write targets — so several
 * controls acting on the same entity share one key, and a second write against an entity
 * already being written is refused. `ScheduleSection`'s five per-Visit controls establish the
 * rule: all five mutate the same Visit and share that Visit's key, while other rows' controls
 * stay live.
 *
 * Both `setPendingKeys` calls construct a NEW `Set` rather than mutating the previous one — a
 * mutated `Set` is referentially identical, React bails out of the re-render, and the control
 * would never visibly disable. The synchronous `inFlight` ref and the unconditional `finally`
 * carry the same rationale as `usePendingWrite` above.
 */
export function useKeyedPendingWrite(): {
  isPending: (key: string) => boolean;
  run: (key: string, write: () => Promise<void>) => Promise<void>;
} {
  const [pendingKeys, setPendingKeys] = useState<ReadonlySet<string>>(() => new Set());
  const inFlight = useRef<Set<string>>(new Set());

  const run = useCallback(async (key: string, write: () => Promise<void>) => {
    if (inFlight.current.has(key)) {
      return;
    }
    inFlight.current.add(key);
    setPendingKeys((previous) => new Set(previous).add(key));
    try {
      await write();
    } finally {
      inFlight.current.delete(key);
      setPendingKeys((previous) => {
        const next = new Set(previous);
        next.delete(key);
        return next;
      });
    }
  }, []);

  const isPending = useCallback((key: string) => pendingKeys.has(key), [pendingKeys]);

  return { isPending, run };
}
